import React, { useEffect, useState } from "react";
import HomeModal from "../Modal";
import { useDispatch, useSelector } from "react-redux";
import { getJobsAction } from "../../../../redux/actions/jobActions";
import { SectionLoader } from "../../../../components/PageLoader";
import EmptyState from "../../../../components/EmptyState";
import { useHistory, useParams, useLocation } from "react-router-dom";
import CustomButton from "../../../../components/CustomButton";
import { ButtonLoader } from "../../../../components/ButtonLoader";
import { saveBusinessRef } from "../../../../redux/actions/businessActions";
import JobCard from "./JobCard";
import JobFilters from "./JobFilters";
import icons from "../../../../assets/icons/icon-collection.svg";

const AllJobs = () => {
  const [page, setPage] = useState(1);
  const history = useHistory();
  const [totalJobs, setTotalJobs] = useState(0);
  const { pathname } = useLocation();
  const [loading, setLoading] = useState(false);
  const [section, setSection] = useState("details");
  const { jobId, refLink } = useParams();
  const [loadingMore, setLoadingMore] = useState(false);
  const jobsPerPage = 20;
  const [openFilterType, setOpenFilterType] = useState(null);
  const [activeFilters, setActiveFilters] = useState({
    jobTier: "",
    datePosted: "",
    jobType: [],
    experienceLevel: [],
  });

  const dispatch = useDispatch();
  const { userData } = useSelector((state) => state.auth);
  const { jobsData, locationFilter } = useSelector(({ job }) => job);
  const jobs = jobsData && jobsData.jobs;

  // Total pages driven purely by backend count
  const totalPages = Math.max(1, Math.ceil(totalJobs / jobsPerPage));

  useEffect(() => {
    const fetchJobs = async () => {
      setLoading(page === 1);
      setLoadingMore(page > 1);

      try {
        const response = await getJobsAction(dispatch, page, jobsPerPage, activeFilters, locationFilter);

        if (response && response.total) {
          setTotalJobs(response.total);
        } else {
          setTotalJobs(0);
        }
      } catch (error) {
        console.error("Error fetching jobs:", error);
      }

      setLoading(false);
      setLoadingMore(false);
    };

    fetchJobs();
  }, [dispatch, page, jobsPerPage, activeFilters, locationFilter]);

  // Reset to page 1 when location filter changes
  useEffect(() => {
    setPage(1);
  }, [locationFilter]);

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setPage(newPage);
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const saveForLater = () => {
    const payload = {
      job_id: jobId,
      status: 1,
      refCode: refLink ? refLink : null,
    };
    const saveLater = async () => {
      await saveBusinessRef(dispatch, page, payload);
    };
    saveLater();
  };

  const handleOpenFilter = (filterType) => setOpenFilterType(filterType);
  const handleCloseFilter = () => setOpenFilterType(null);

  const handleApplyFilter = (filterType, value) => {
    setActiveFilters((prev) => ({ ...prev, [filterType]: value }));
    setPage(1);
  };

  const handleClearAllFilters = () => {
    setActiveFilters({ jobTier: "", datePosted: "", jobType: [], experienceLevel: [] });
    setPage(1);
  };

  const hasActiveFilter = (filterType) => {
    const value = activeFilters[filterType];
    if (Array.isArray(value)) return value.length > 0;
    return value !== "" && value !== null && value !== undefined;
  };

  const getFilterLabel = (filterType) => {
    const value = activeFilters[filterType];

    if (!hasActiveFilter(filterType)) {
      switch (filterType) {
        case "jobTier":         return "Job Tier";
        case "datePosted":      return "Date Posted";
        case "jobType":         return "Job Type";
        case "experienceLevel": return "Experience Level";
        default:                return "";
      }
    }

    if (Array.isArray(value)) {
      return value.length === 1 ? value[0] : `${value.length} selected`;
    }

    switch (filterType) {
      case "jobTier":
        return `Tier ${value}`;
      case "datePosted":
        if (value === "today")  return "Today";
        if (value === "3days")  return "Last 3 days";
        if (value === "7days")  return "Last 7 days";
        if (value === "14days") return "Last 14 days";
        return value;
      default:
        return value;
    }
  };

  const anyFilterActive =
    hasActiveFilter("jobTier") ||
    hasActiveFilter("datePosted") ||
    hasActiveFilter("jobType") ||
    hasActiveFilter("experienceLevel");

  return (
    <div className="row position-relative mt-3 align-items-stretch">
      {jobId && (
        <HomeModal
          setSection={setSection}
          section={section}
          open={true}
          handleClose={() => history.push("/home")}
          isBusiness={pathname.includes("biz") || pathname.includes("coy")}
          isFromCompany={!pathname.includes("coy")}
          saveForLater={saveForLater}
        />
      )}

      {userData?.country !== "160" ? (
        <div
          className="col-12 d-flex flex-column align-items-center justify-content-center"
          style={{ minHeight: "400px", padding: "40px 20px" }}
        >
          <div
            className="d-flex flex-column align-items-center justify-content-center text-center"
            style={{
              maxWidth: "600px",
              backgroundColor: "#f8f9fa",
              borderRadius: "16px",
              padding: "48px 32px",
              boxShadow: "0 4px 6px rgba(0,0,0,0.07)",
            }}
          >
            <div style={{ fontSize: "64px", marginBottom: "24px", opacity: 0.8 }}>🌍</div>
            <h3 style={{ fontSize: "24px", fontWeight: "600", color: "#2c3e50", marginBottom: "16px", lineHeight: "1.4" }}>
              workbrook hasn&apos;t launched in your country yet
            </h3>
            <p style={{ fontSize: "16px", color: "#6c757d", lineHeight: "1.6", marginBottom: "0" }}>
              You can explore other features on Workbrook, or if you&apos;re
              hiring, post a job to get started. You can also explore
              opportunities in other locations or remote roles.{" "}
              <a href="/onboarding" style={{ color: "#0d6efd" }}>
                Visit our onboarding page
              </a>{" "}
              to learn more.
            </p>
          </div>
        </div>
      ) : (
        <>
          {/* ── Filter Tabs ─────────────────────────────────────── */}
          <div className="col-12 mb-3">
            <div
              className="d-flex align-items-center justify-content-between"
              style={{ gap: "10px", flexWrap: "wrap" }}
            >
              <div
                className="d-flex align-items-center"
                style={{ gap: "10px", overflowX: "auto", paddingBottom: "8px", flexWrap: "wrap" }}
              >
                {["jobTier", "datePosted", "jobType", "experienceLevel"].map((filterType) => (
                  <button
                    key={filterType}
                    onClick={() => handleOpenFilter(filterType)}
                    style={{
                      padding: "8px 16px",
                      borderRadius: "20px",
                      border: hasActiveFilter(filterType) ? "2px solid #3A6ED4" : "1px solid #ddd",
                      backgroundColor: hasActiveFilter(filterType) ? "#E8F0FE" : "#fff",
                      color: hasActiveFilter(filterType) ? "#3A6ED4" : "#333",
                      fontSize: "14px",
                      fontWeight: hasActiveFilter(filterType) ? "600" : "400",
                      cursor: "pointer",
                      transition: "all 0.2s ease",
                      whiteSpace: "nowrap",
                      display: "flex",
                      alignItems: "center",
                      gap: "6px",
                    }}
                    onMouseEnter={(e) => {
                      if (!hasActiveFilter(filterType)) {
                        e.currentTarget.style.borderColor = "#3A6ED4";
                        e.currentTarget.style.backgroundColor = "#f8f9fa";
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!hasActiveFilter(filterType)) {
                        e.currentTarget.style.borderColor = "#ddd";
                        e.currentTarget.style.backgroundColor = "#fff";
                      }
                    }}
                  >
                    {getFilterLabel(filterType)}
                    {hasActiveFilter(filterType) && (
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <polyline points="20 6 9 17 4 12" />
                      </svg>
                    )}
                  </button>
                ))}
              </div>

              {anyFilterActive && (
                <CustomButton
                  handleClick={handleClearAllFilters}
                  text="Clear All"
                  backgroundColor="#dc3545"
                  color="#fff"
                  additionalClass="px-3"
                  style={{ fontSize: "14px", padding: "8px 16px" }}
                />
              )}
            </div>
          </div>

          {/* ── Job Cards ───────────────────────────────────────── */}
          {loading && jobs && !jobs[0] && <SectionLoader />}
          {!loading && jobs && !jobs[0] && (
            <EmptyState text="There are no jobs matching your filters" />
          )}
          {jobs && jobs[0] && (
            <>
              {jobs.map((job, index) => (
                <div
                  style={{ padding: "10px", maxHeight: "280px" }}
                  key={index.toString()}
                  className="col-12 col-md-6"
                >
                  <JobCard job={job} />
                </div>
              ))}
            </>
          )}

          {/* ── Pagination ──────────────────────────────────────── */}
          {totalPages >= 1 && (
            <div className="container" style={{ marginTop: "5%" }}>
              <div className="row clearfix">
                <div className="mx-auto col-md-2 mt-3 w-75" />

                <div className="mx-auto col-md-2 mt-3 w-75">
                  <CustomButton
                    handleClick={() => handlePageChange(page - 1)}
                    text={loadingMore ? <ButtonLoader /> : "Prev"}
                    disabled={page === 1 || loading}
                  />
                </div>

                <div className="col-md-2 text-center" style={{ marginTop: "2.5%" }}>
                  <p style={{ marginBottom: "4px", fontWeight: "500" }}>
                    Page {page} of {totalPages}
                  </p>
                  {totalJobs > 0 && (
                    <p style={{ fontSize: "12px", color: "#999", marginBottom: "0" }}>
                      {totalJobs} jobs
                    </p>
                  )}
                </div>

                <div className="mx-auto col-md-2 mt-3 w-75">
                  <CustomButton
                    handleClick={() => handlePageChange(page + 1)}
                    text={loadingMore ? <ButtonLoader /> : "Next"}
                    disabled={loading || page === totalPages}
                  />
                </div>

                <div className="mx-auto col-md-2 mt-3 w-75" />
              </div>
            </div>
          )}

          {/* ── Filter Modal ─────────────────────────────────────── */}
          {openFilterType && (
            <JobFilters
              filterType={openFilterType}
              open={true}
              onClose={handleCloseFilter}
              onApplyFilter={handleApplyFilter}
              currentValue={activeFilters[openFilterType]}
            />
          )}
        </>
      )}
    </div>
  );
};

export default AllJobs;